import React from "react";
import { Box, Typography, Container, Chip, Grid, Divider, Button } from "@mui/material";
import ArrowOutwardRoundedIcon from "@mui/icons-material/ArrowOutwardRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import CodeRoundedIcon from "@mui/icons-material/CodeRounded";
import DesignServicesRoundedIcon from "@mui/icons-material/DesignServicesRounded";
import SupportAgentRoundedIcon from "@mui/icons-material/SupportAgentRounded";
import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import ContactForm from "../components/ContactForm";
import SEO from "../components/SEO";

const ABOUT_SCHEMA = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "AboutPage",
      "@id": "https://studio.wiyoutech.in/about#page",
      "name": "About WiYou! Tech Studios | Jaipur, Rajasthan",
      "description": "Learn about WiYou! Tech Studios, a digital technology studio based in Jaipur, Rajasthan, creating high-performance websites, custom web applications, digital menus, and design systems.",
      "url": "https://studio.wiyoutech.in/about",
      "isPartOf": { "@type": "WebSite", "@id": "https://studio.wiyoutech.in/#website" }
    },
    {
      "@type": "ProfessionalService",
      "@id": "https://studio.wiyoutech.in/#organization",
      "name": "WiYou! Tech Studios",
      "url": "https://studio.wiyoutech.in",
      "logo": "https://studio.wiyoutech.in/wiyou-logo.png",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Jaipur",
        "addressRegion": "Rajasthan",
        "addressCountry": "IN"
      },
      "areaServed": [
        { "@type": "City", "name": "Jaipur" },
        { "@type": "State", "name": "Rajasthan" },
        { "@type": "Country", "name": "India" }
      ],
      "knowsAbout": [
        "Web Development",
        "Website Design",
        "Custom Web Applications",
        "Digital Menus",
        "QR Menu Development",
        "Restaurant Menu Design",
        "UI/UX Design",
        "Technical SEO"
      ]
    }
  ]
};

const MotionBox = motion(Box);
const MotionImg = motion("img");

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] },
  }),
};

const stagger = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.08 },
  },
};

const serviceIcons = [
  <LanguageRoundedIcon />,
  <LanguageRoundedIcon />,
  <AutoAwesomeRoundedIcon />,
  <SupportAgentRoundedIcon />,
  <DesignServicesRoundedIcon />,
  <DesignServicesRoundedIcon />,
  <CodeRoundedIcon />,
  <RocketLaunchRoundedIcon />,
];

const About = () => {
  const navigate = useNavigate();

  const whatWeDo = [
    "CMS – Content Management Systems",
    "Custom Domains & DNS Setup",
    "SEO – Search Engine Optimisation",
    "Site Maintenance & Monitoring",
    "UI / UX Design",
    "Web Design",
    "Web Development",
    "Web Hosting & Deployment",
  ];

  const whyChoose = [
    {
      title: "Tailored Solutions",
      desc: "Every project is shaped around your brand, goals, and audience. No templates—just solutions that actually fit.",
    },
    {
      title: "Modern Technology",
      desc: "We build with current, reliable web stacks to keep your site fast, secure, and easy to scale over time.",
    },
    {
      title: "Collaborative Process",
      desc: "You’re involved at every stage—from wireframes to launch—so the final product feels like you, not us.",
    },
    {
      title: "Ongoing Support",
      desc: "We don’t disappear after launch. We’re here to maintain, refine, and grow your digital presence with you.",
    },
  ];

  const editorialInk = "#2B211C";
const editorialMuted = "#75675E";
const editorialAccent = "#A06E50";
const editorialLine = "#DCD4CC";

  return (
    <Box
      sx={{
        bgcolor: "#F7F4EF00",
        color: "#2B211C",
        overflow: "hidden",
        pt: { xs: 13, md: 17 },
        pb: 10,
      }}
    >
      <SEO
        title="About WiYou! Tech Studios | Web & Digital Studio Jaipur"
        description="WiYou! Tech Studios is a Jaipur-based digital technology studio creating modern websites, custom web apps, digital menus, and design systems for businesses across India."
        keywords={[
          "about WiYou Tech Studios",
          "web development company Jaipur",
          "digital technology studio Jaipur",
          "website design agency Rajasthan",
          "software studio Jaipur",
          "custom web applications India"
        ]}
        canonicalUrl="https://studio.wiyoutech.in/about"
        schema={ABOUT_SCHEMA}
      />
      <Container maxWidth="xl">
        {/* HERO */}
        <MotionBox
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={0}
          sx={{
            minHeight: { md: "72vh" },
            display: "grid",
            alignItems: "end",
            pb: { xs: 8, md: 12 },
          }}
        >
          <Grid container spacing={{ xs: 5, md: 8 }} alignItems="end">
            <Grid item xs={12} md={8}>
              <Chip
                label="About WiYou! Tech"
                variant="outlined"
                sx={{
                  mb: 3,
                  borderColor: editorialInk,
                  color: editorialInk,
                  bgcolor: "transparent",
                  borderRadius: 999,
                  px: 1.5,
                  letterSpacing: ".16em",
                  textTransform: "uppercase",
                  fontSize: 10,
                  fontWeight: 700,
                }}
              />

              <Typography
                className="editorial-serif"
                component="h1"
                sx={{
                  fontSize: { xs: "3.2rem", sm: "4.8rem", md: "7.2rem" },
                  lineHeight: { xs: 0.98, md: 0.9 },
                  letterSpacing: "-.065em",
                  fontWeight: 700,
                  maxWidth: 980,
                }}
              >
                Empowering your web presence with clarity & confidence.
              </Typography>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ borderTop: `1px solid ${editorialLine}`, pt: 2 }}>
                <Typography
                  sx={{
                    color: editorialMuted,
                    lineHeight: 1.8,
                    fontSize: { xs: 14, md: 15 },
                    maxWidth: 370,
                  }}
                >
                  WiYou! Tech blends strategy, design, and development to
                  create websites that feel modern, thoughtful, and genuinely
                  useful—for both you and your audience.
                </Typography>

                <Button
                  href="#studio"
                  endIcon={<ArrowDownwardRoundedIcon />}
                  sx={{
                    mt: 4,
                    px: 0,
                    color: editorialInk,
                    textTransform: "none",
                    fontWeight: 700,
                    "&:hover": { bgcolor: "transparent", color: editorialAccent },
                  }}
                >
                  Explore the studio
                </Button>
              </Box>
            </Grid>
          </Grid>
        </MotionBox>

        <Divider sx={{ borderColor: editorialLine }} />

        {/* STUDIO INTRO */}
        <Box
          id="studio"
          sx={{ py: { xs: 8, md: 13 } }}
        >
          <Grid container spacing={{ xs: 5, md: 10 }} alignItems="center">
            <Grid item xs={12} md={5}>
              <MotionBox
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                custom={0.05}
                sx={{
                  position: "relative",
                  maxWidth: 520,
                  mx: { xs: "auto", md: 0 },
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: -14,
                    left: -14,
                    width: "100%",
                    height: "100%",
                    border: `1px solid ${editorialLine}`,
                    zIndex: 0,
                  }}
                />
                <MotionImg
                  src="/assets/logo.png"
                  alt="WiYou! Tech"
                  initial={{ opacity: 0, scale: 0.97 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8 }}
                  sx={{
                    position: "relative",
                    zIndex: 1,
                    display: "block",
                    width: "100%",
                    bgcolor: "#fff",
                    objectFit: "cover",
                    aspectRatio: "1 / 1",
                    filter: "grayscale(1)",
                  }}
                />
              </MotionBox>
            </Grid>

            <Grid item xs={12} md={7}>
              <MotionBox
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                custom={0.12}
              >
                <Typography
                  sx={{
                    fontSize: 10,
                    letterSpacing: ".2em",
                    textTransform: "uppercase",
                    fontWeight: 800,
                    color: editorialAccent,
                    mb: 2,
                  }}
                >
                  01 — The studio
                </Typography>

                <Typography
                  className="editorial-serif"
                  sx={{
                    fontSize: { xs: "2.3rem", md: "3.7rem" },
                    lineHeight: 1,
                    letterSpacing: "-.045em",
                    mb: 4,
                  }}
                >
                  More than a site online.
                  <br />
                  A digital home.
                </Typography>

                <Typography sx={{ color: editorialMuted, lineHeight: 1.9, maxWidth: 690 }}>
                  At <strong>WiYou! Tech Studios</strong>, our focus is simple:
                  build websites that perform well, look sharp, and feel
                  effortless to use. We work with businesses, creators, and
                  teams who want more than just “a site online”—they want a
                  digital home that actually supports their goals.
                </Typography>

                <Typography sx={{ color: editorialMuted, lineHeight: 1.9, maxWidth: 690, mt: 2.5 }}>
                  From the first strategy call to post-launch refinement, we
                  stay close to your vision. We listen, translate your ideas
                  into clear experiences, and ship products that balance
                  design, performance, and practicality.
                </Typography>
              </MotionBox>
            </Grid>
          </Grid>
        </Box>

        {/* INTRO */}
        <MotionBox
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: .25 }}
          sx={{ borderTop: "1px solid #DCD4CC", mt: { xs: 9, md: 15 }, pt: { xs: 6, md: 9 } }}
        >
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "180px 1fr" }, gap: 6 }}>
            <Typography sx={{ fontSize: 12, letterSpacing: ".14em", textTransform: "uppercase", color: "#A06E50", fontWeight: 700 }}>
              01 / The studio
            </Typography>
            <Box>
              <Typography
                sx={{
                  maxWidth: 1000,
                  fontFamily: "'Gilda Display', Georgia, serif",
                  fontSize: { xs: 32, md: 54 },
                  lineHeight: 1.08,
                  letterSpacing: "-.045em",
                }}
              >
                We make the web feel simpler, sharper, and more human.
              </Typography>
              <Typography sx={{ maxWidth: 820, mt: 4, color: "#75675E", fontSize: 17, lineHeight: 1.8 }}>
                At WiYou! Tech Studios, our focus is simple: build websites that perform well, look sharp,
                and feel effortless to use. We work with businesses, creators, and teams who want more than
                just “a site online”—they want a digital home that actually supports their goals.
              </Typography>
              <Typography sx={{ maxWidth: 820, mt: 2.5, color: "#75675E", fontSize: 17, lineHeight: 1.8 }}>
                From the first strategy call to post-launch refinement, we stay close to your vision. We listen,
                translate your ideas into clear experiences, and ship products that balance design, performance,
                and practicality.
              </Typography>
            </Box>
          </Box>
        </MotionBox>

        {/* WHAT WE DO */}
        <Box sx={{ mt: { xs: 12, md: 18 } }}>
          <MotionBox variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, amount: .2 }}>
            <Typography sx={{ fontSize: 12, letterSpacing: ".14em", textTransform: "uppercase", color: "#A06E50", fontWeight: 700 }}>
              02 / What we do
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3, mt: 3 }}>
              <Typography sx={{ fontFamily: "'Gilda Display', Georgia, serif", fontSize: { xs: 42, md: 68 }, lineHeight: .95, letterSpacing: "-.055em" }}>
                Everything your<br />digital presence needs.
              </Typography>
              <Typography sx={{ maxWidth: 520, alignSelf: "end", color: "#75675E", fontSize: 16, lineHeight: 1.75 }}>
                From the first visual direction to launch, hosting, optimisation, and maintenance, we keep the
                digital experience connected instead of treating every part as a separate task.
              </Typography>
            </Box>
          </MotionBox>

<MotionBox
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: .15 }}
            sx={{ 
              display: "grid", 
              gridTemplateColumns: { xs: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }, 
              gap: { xs: 1.5, md: 2 }, 
              mt: 7 
            }}
          >
            {whatWeDo.map((item, index) => (
              <MotionBox
                key={item}
                variants={fadeUp}
                sx={{
                  minHeight: { xs: 170, md: 210 },
                  p: { xs: 2, md: 3 },
                  bgcolor: "#FCFAF6",
                  border: "1px solid #DCD4CC",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  transition: "transform .35s ease, border-color .35s ease, background-color .35s ease",
                  "&:hover": { transform: "translateY(-5px)", borderColor: "#A06E50", bgcolor: "#FFFDF8" },
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Box sx={{ width: 38, height: 38, borderRadius: "50%", bgcolor: "#F0E9E1", color: "#3E2723", display: "grid", placeItems: "center" }}>
                    {React.cloneElement(serviceIcons[index], { sx: { fontSize: 19 } })}
                  </Box>
                  <Typography sx={{ fontSize: 11, color: "#A06E50", letterSpacing: ".08em" }}>
                    {String(index + 1).padStart(2, "0")}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: { xs: 15, md: 17 }, fontWeight: 650, lineHeight: 1.25, maxWidth: 230 }}>
                  {item}
                </Typography>
              </MotionBox>
            ))}
          </MotionBox>
        </Box>

        {/* WHY CHOOSE */}
        <Box sx={{ mt: { xs: 13, md: 19 } }}>
          <MotionBox variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, amount: .2 }}>
            <Typography sx={{ fontSize: 12, letterSpacing: ".14em", textTransform: "uppercase", color: "#A06E50", fontWeight: 700 }}>
              03 / Why work with us
            </Typography>
            <Typography sx={{ mt: 2, maxWidth: 780, fontFamily: "'Gilda Display', Georgia, serif", fontSize: { xs: 42, md: 70 }, lineHeight: .95, letterSpacing: "-.055em" }}>
              Thoughtful by default.
            </Typography>
          </MotionBox>

<MotionBox
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: .15 }}
            sx={{ mt: 7, borderTop: "1px solid #DCD4CC" }}
          >
            {whyChoose.map((item, index) => (
              <MotionBox
                key={item.title}
                variants={fadeUp}
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "55px 1fr", md: "90px 1fr 1.25fr" },
                  gap: { xs: 2, md: 5 },
                  alignItems: "start",
                  py: { xs: 3.5, md: 5 },
                  borderBottom: "1px solid #DCD4CC",
                  transition: "padding .35s ease, background-color .35s ease",
                  "&:hover": { bgcolor: "#FCFAF6", px: { xs: 2, md: 3 } },
                }}
              >
                {/* Number Indicator */}
                <Typography sx={{ fontSize: 12, color: "#A06E50", pt: .8 }}>
                  {String(index + 1).padStart(2, "0")}
                </Typography>

                {/* Title */}
                <Typography 
                  sx={{ 
                    fontFamily: "'Gilda Display', Georgia, serif", 
                    fontSize: { xs: "1.45rem", md: "2.375rem" }, 
                    lineHeight: 1.05, 
                    letterSpacing: "-.035em" 
                  }}
                >
                  {item.title}
                </Typography>

                {/* Description - Spans full width on mobile via grid column placement */}
                <Typography 
                  sx={{ 
                    color: "#75675E", 
                    fontSize: { xs: "0.84rem", md: "0.9375rem" }, 
                    lineHeight: 1.75, 
                    mt: { xs: 1.5, md: .5 },
                    gridColumn: { xs: "1 / -1", md: "auto" }, // Wraps description underneath on mobile
                    pl: { xs: "55px", md: 0 },                  // Aligns nicely with the text column above on mobile
                  }}
                >
                  {item.desc}
                </Typography>
              </MotionBox>
            ))}
          </MotionBox>
        </Box>

        {/* VISION */}
        <MotionBox
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: .25 }}
          sx={{
            mt: { xs: 13, md: 19 },
            bgcolor: "#2B211C",
            color: "#FFFDF8",
            p: { xs: 4, sm: 6, md: 9 },
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Typography sx={{ position: "absolute", right: { xs: 18, md: 45 }, top: { xs: 20, md: 35 }, fontSize: 11, letterSpacing: ".14em", color: "#B9AAA0" }}>
            04 / Our vision
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: ".7fr 1.3fr" }, gap: { xs: 5, md: 10 }, alignItems: "end" }}>
            <Box>
              <Typography sx={{ fontSize: 12, letterSpacing: ".14em", textTransform: "uppercase", color: "#B9AAA0" }}>
                Our vision
              </Typography>
              <Typography sx={{ mt: 2, fontFamily: "'Gilda Display', Georgia, serif", fontSize: { xs: 48, md: 76 }, lineHeight: .92, letterSpacing: "-.06em" }}>
                Better web.<br />Less friction.
              </Typography>
            </Box>
            <Typography sx={{ maxWidth: 700, color: "#D8CEC7", fontSize: { xs: 17, md: 21 }, lineHeight: 1.75 }}>
              We imagine a web where experiences are fast, human, and thoughtful. At WiYou! Tech, we work
              towards that by building websites that remove friction, feel intuitive, and support real people
              doing real work—every single day.
            </Typography>
          </Box>
        </MotionBox>

        {/* CTA */}
        <MotionBox
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: .25 }}
          sx={{ mt: { xs: 11, md: 16 }, py: { xs: 5, md: 8 }, borderBottom: "1px solid #DCD4CC" }}
        >
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr auto" }, gap: 5, alignItems: "end" }}>
            <Box>
              <Typography sx={{ fontSize: 12, letterSpacing: ".14em", textTransform: "uppercase", color: "#A06E50", fontWeight: 700 }}>
                Let’s get started
              </Typography>
              <Typography sx={{ mt: 2, maxWidth: 900, fontFamily: "'Gilda Display', Georgia, serif", fontSize: { xs: 42, md: 66 }, lineHeight: .96, letterSpacing: "-.055em" }}>
                Bring the rough idea.<br />We’ll shape the rest.
              </Typography>
              <Typography sx={{ mt: 3, maxWidth: 720, color: "#75675E", fontSize: 16, lineHeight: 1.75 }}>
                Whether you’re planning your first website or improving an existing one, we’d love to partner
                with you. Share your ideas, challenges, or even a rough sketch—together, we’ll turn it into a
                clear, reliable, and beautiful web experience.
              </Typography>
            </Box>
            <Button
              onClick={() => navigate("/pricing")}
              endIcon={<ArrowOutwardRoundedIcon />}
              sx={{
                width: { xs: "100%", md: "auto" },
                minWidth: 180,
                px: 3,
                py: 1.6,
                borderRadius: 99,
                bgcolor: "#A06E50",
                color: "#FFFDF8",
                textTransform: "none",
                fontWeight: 700,
                boxShadow: "none",
                "&:hover": { bgcolor: "#8E5E43", boxShadow: "none" },
              }}
            >
              Start a project
            </Button>
          </Box>
        </MotionBox>

        {/* CONTACT */}
        <Box sx={{ mt: { xs: 10, md: 14 } }}>
          <ContactForm />
        </Box>
      </Container>
    </Box>
  );
};

export default About;
